import { Icon, Modal, Select } from '@seeqdev/qomponents';
import { useTranslation } from 'react-i18next';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Checkbox } from '@/core/Checkbox.atom';
import type { SelectOption } from '@/formbuilder/SelectFormComponent';

export interface TreemapConfigurationModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (selectedSignal: any, selectedColumn: any, treemapSquareScaling: boolean, isUniformSizing: boolean) => void;
  selectOptionsSignal: SelectOption<any>[];
  selectOptionsColumn: SelectOption<any>[];
  proportionBySignal: string | undefined;
  proportionByColumn: string | undefined;
  isUniformSizing: boolean;
  treemapSquareScalingInverse: boolean;
}
export const TreemapConfigurationModal: React.FunctionComponent<TreemapConfigurationModalProps> = ({
  show,
  onClose,
  onSubmit,
  selectOptionsSignal,
  selectOptionsColumn,
  proportionBySignal,
  proportionByColumn,
  isUniformSizing,
  treemapSquareScalingInverse,
}) => {
  const { t } = useTranslation();

  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [isUniform, setIsUniform] = useState<boolean>(isUniformSizing);
  const [squareScalingInverse, setSquareScalingInverse] = useState(treemapSquareScalingInverse);

  const [selectedSignal, setSelectedSignal] = useState<SelectOption<any> | undefined>(
    () => selectOptionsSignal.find((opt) => opt.value === proportionBySignal) || undefined,
  );

  const [selectedColumn, setSelectedColumn] = useState<SelectOption<any> | undefined>(
    () => selectOptionsColumn.find((opt) => opt.value === proportionByColumn) || undefined,
  );

  useEffect(() => {
    if ((!selectedSignal || !selectedColumn) && !isFormDirty) {
      setIsUniform(true);
    }
  }, [selectedSignal, selectedColumn, isFormDirty]);

  useEffect(() => {
    if (show) {
      const matchSignal = selectOptionsSignal.find((opt) => opt.value === proportionBySignal) || undefined;
      const matchColumn = selectOptionsColumn.find((opt) => opt.value === proportionByColumn) || undefined;
      setSelectedSignal(matchSignal);
      setSelectedColumn(matchColumn);
      setIsUniform(isUniformSizing);
      setSquareScalingInverse(treemapSquareScalingInverse);
      setIsFormDirty(false);
    }
  }, [
    show,
    proportionBySignal,
    proportionByColumn,
    isUniformSizing,
    treemapSquareScalingInverse,
    selectOptionsSignal,
    selectOptionsColumn,
  ]);

  const handleSelectSignal = (selected: SelectOption<any>) => {
    setIsFormDirty(true);
    setSelectedSignal(selected);
  };

  const handleSelectColumn = (selected: SelectOption<any>) => {
    setIsFormDirty(true);
    setSelectedColumn(selected);
  };

  const handleTreemapSquareInverseScalingChange = (inverseScaling: boolean) => {
    setIsFormDirty(true);
    setSquareScalingInverse(inverseScaling);
  };

  const handleUniformSizingClick = () => {
    setIsFormDirty(true);
    setIsUniform((prev) => !prev);
  };

  return (
    <Modal
      size="3xl"
      open={show}
      title={t('TREEMAP.CONFIGURE_TREEMAP')}
      subtitle={t('TREEMAP.CONFIGURE_TREEMAP_DESCRIPTION')}
      cancelButtonLabel={t('CANCEL')}
      onClose={() => {
        onClose();
      }}
      submitButtonLabel={t('SAVE')}
      hideSubmitButton={false}
      disableSubmitButton={selectedSignal === undefined || selectedColumn === undefined}
      onSubmit={() => {
        onSubmit(selectedSignal?.value, selectedColumn?.value, squareScalingInverse, isUniform);
      }}>
      <div className="flexRowContainer">
        <div className="max-width-400 mb50">
          <div className="p5 ml10 mr10">
            <div>
              <div className="pb10">
                <strong>{t('TREEMAP.PROPORTIONS')}</strong>
                <Icon
                  icon="fa fa-question-circle"
                  tooltip={`${t('TREEMAP.PROPORTION_BY_HELP_TEXT')}`}
                  tooltipDelay={0}
                  testId="treemap-configure-help-icon"
                />
              </div>

              <div className="pb10">
                <Checkbox
                  id="treemap-uniform-sizing"
                  label="TREEMAP.UNIFORM_SIZING"
                  isChecked={isUniform}
                  onClick={handleUniformSizingClick}
                />
              </div>
            </div>

            <div data-testid="treemap-select-signal">
              <label>{t('TREEMAP.CHOOSE_SIGNAL')}</label>
              <Select
                extraClassNames={`width-250 ${isUniform ? 'disabled' : ''}`}
                menuPlacement="bottom"
                menuPortalTarget={document.body}
                value={selectedSignal}
                options={selectOptionsSignal}
                isDisabled={isUniform}
                onChange={(selected) => handleSelectSignal(selected)}
              />
            </div>
          </div>

          <div className="p5 ml10 mr10" data-testid="treemap-select-column">
            <label>{t('TREEMAP.CHOOSE_COLUMN')}</label>
            <Select
              extraClassNames={`width-250 ${isUniform ? 'disabled' : ''}`}
              menuPlacement="bottom"
              menuPortalTarget={document.body}
              value={selectedColumn}
              options={selectOptionsColumn}
              isDisabled={isUniform}
              onChange={(selected) => handleSelectColumn(selected)}
            />
          </div>

          <div className="p5 ml10 mr10">
            <label className="mb5">{t('TREEMAP.SQUARE_SIZE_REPRESENTS')}</label>
            <div>
              <Checkbox
                id="treemap-square-size-regular"
                type="radio"
                disabled={isUniform}
                label={t('TREEMAP.LARGER_VALUES')}
                isChecked={!squareScalingInverse}
                onChange={() => handleTreemapSquareInverseScalingChange(false)}
              />
              <Checkbox
                id="treemap-square-size-inverse"
                type="radio"
                disabled={isUniform}
                label={t('TREEMAP.SMALLER_VALUES')}
                isChecked={squareScalingInverse}
                onChange={() => handleTreemapSquareInverseScalingChange(true)}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
